import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const empty = { name: "", sku: "", category: "", stock: 0, unit: "pcs", location: "" };

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/inventory");
      setItems(data || []);
    } catch (e) { toast.error("Failed to load inventory"); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/inventory", { ...form, stock: Number(form.stock) });
      toast.success("Item added");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/inventory/${id}`);
      toast.success("Item removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const filtered = items.filter((i) => {
    const q = search.toLowerCase();
    return !q || i.name.toLowerCase().includes(q) || i.sku.toLowerCase().includes(q) || i.category.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-8" data-testid="inventory-page">
      <header className="flex flex-wrap justify-between gap-4 items-end">
        <div>
          <span className="overline">/ Inventory</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">Stock & Equipment</h1>
          <p className="text-muted-foreground mt-2">Track stock levels across all depots and project sites.</p>
        </div>
        <div className="flex gap-3">
          <Input
            placeholder="Search SKU, item, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm h-11 w-64"
            data-testid="inventory-search"
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm h-11 bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground" data-testid="inventory-add-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm" data-testid="inventory-add-dialog">
              <DialogHeader>
                <DialogTitle>Add inventory item</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { k: "name", l: "Item name", c: 2 },
                  { k: "sku", l: "SKU" },
                  { k: "category", l: "Category" },
                  { k: "stock", l: "Stock", t: "number" },
                  { k: "unit", l: "Unit" },
                  { k: "location", l: "Location", c: 2 },
                ].map((f) => (
                  <div key={f.k} className={f.c === 2 ? "col-span-2" : ""}>
                    <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">{f.l}</Label>
                    <Input
                      type={f.t || "text"}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      className="rounded-sm"
                      data-testid={`inventory-input-${f.k}`}
                    />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="inventory-cancel-btn">Cancel</Button>
                <Button onClick={create} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="inventory-save-btn">Save Item</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <Card className="rounded-sm overflow-hidden">
        <Table data-testid="inventory-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Item</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">No items found.</TableCell></TableRow>
            )}
            {filtered.map((i) => (
              <TableRow key={i.id} data-testid={`inventory-row-${i.sku}`}>
                <TableCell className="font-semibold">{i.name}</TableCell>
                <TableCell className="text-muted-foreground tabular-nums">{i.sku}</TableCell>
                <TableCell>{i.category}</TableCell>
                <TableCell className="text-right tabular-nums">{i.stock} {i.unit}</TableCell>
                <TableCell>{i.location}</TableCell>
                <TableCell>
                  <Badge
                    className={`rounded-sm font-medium ${
                      i.status === "in_stock" ? "bg-primary/15 text-primary border-primary/30 hover:bg-primary/20" :
                      i.status === "low_stock" ? "bg-amber-500/15 text-amber-700 border-amber-500/30 hover:bg-amber-500/20" :
                      "bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/20"
                    }`}
                    variant="outline"
                  >
                    {i.status.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => remove(i.id)} data-testid={`inventory-delete-${i.sku}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
